import React, { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useGetClientOverviewQuery,
  useGetClientSubcampaignDetailQuery,
} from "../../store/apis/clientPortalApi";
import DetailSectionsViewClient from "../../components/client/DetailSectionsViewClient";
import {
  ClientCard,
  ClientHeader,
  ClientPage,
  ClientTrail,
  PlatformTag,
  StatusPill,
} from "../../components/client/ClientUiViewClient";
import {
  ClientError,
  ClientMessage,
} from "../../components/client/ClientStatesViewClient";
import DetailSkeletonViewClient, {
  DetailSectionsLoading,
} from "../../components/client/DetailSkeletonViewClient";
import MetricChartsViewClient, {
  buildMetricDays,
  goalInRange,
  lifetimeTotals,
  sumDays,
} from "../../components/client/MetricChartsViewClient";
import {
  DateRange,
  DateRangeBar,
  LIFETIME,
  resolveRange,
} from "../../components/client/DateRangePickerViewClient";
import {
  moneyStatItems,
  splitMoneyItems,
} from "../../components/client/MoneyViewClient";
import {
  GoalPanel,
  StatPanels,
} from "../../components/client/StatPanelsViewClient";
import StickyStatsViewClient, {
  performanceStatItems,
} from "../../components/client/StickyStatsViewClient";
import {
  TITLE_SOURCE_ATTR,
  transitionLinkProps,
  useClientTransition,
} from "../../components/client/clientTransitionViewClient";
import {
  campaignPath,
  costPer,
  formatDate,
  formatPercent,
  isPerThousand,
  subcampaignPath,
} from "../../components/client/clientFormatViewClient";

/** Una subcampaña: cuánto lleva de su objetivo, sus cifras y su evolución. */
const SubcampaignViewClient: React.FC = () => {
  const { campaignId, subcampaignId } = useParams<{
    campaignId: string;
    subcampaignId: string;
  }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { data, isLoading, isError, refetch } = useGetClientOverviewQuery();
  const go = useClientTransition();
  // El detalle llega aparte: la página se pinta ya y sus apartados aparecen después.
  const { data: detail, isLoading: detailLoading } =
    useGetClientSubcampaignDetailQuery(subcampaignId ?? "", {
      skip: !subcampaignId,
    });
  // Las fechas de arriba: todo lo de la página es de ese rango.
  const [range, setRange] = useState<DateRange>(LIFETIME);

  if (isLoading) return <DetailSkeletonViewClient />;
  if (isError || !data) return <ClientError onRetry={refetch} />;

  const campaign = data.campaigns.find((c) => c.id === campaignId);
  const sub = campaign?.subcampaigns.find((s) => s.id === subcampaignId);
  if (!campaign || !sub)
    return (
      <ClientMessage
        title={t("clientView.subcampaign.notFound")}
        action={{
          label: campaign ? campaign.name : t("clientView.home"),
          to: campaign ? campaignPath(campaign.id) : "/",
        }}
      />
    );

  const asOf = data.asOfDate;
  const days = buildMetricDays([sub], sub.kpi, asOf, detail?.daily);
  const bounds = {
    min: days[0]?.date ?? asOf,
    max: days[days.length - 1]?.date ?? asOf,
  };
  const { from: dateFrom, to: dateTo } = resolveRange(range, bounds, asOf);
  const isLifetime = range.preset === "lifetime";
  const inRange = isLifetime
    ? lifetimeTotals([sub], sumDays(days, dateFrom, dateTo))
    : sumDays(days, dateFrom, dateTo);
  // El objetivo en esas fechas, con lo conseguido del resumen (exacto desde el
  // primer momento, aunque el detalle aún no haya llegado).
  const goal = goalInRange(
    buildMetricDays([sub], sub.kpi, asOf),
    sub.kpi,
    dateFrom,
    dateTo
  );
  const progress = goal.planned ? goal.delivered / goal.planned : null;
  const performance = performanceStatItems(inRange, t, language);
  const totals = {
    ...inRange,
    budget: sub.budget ? inRange.plannedInvestment : null,
  };
  const money = splitMoneyItems(
    moneyStatItems(totals, campaign.currency, t, language, {
      model: sub.pricingModel,
      agreedUnitCost: sub.agreedUnitCost,
      // El coste por unidad de esas fechas: su inversión entre su objetivo.
      unitCost: costPer(
        inRange.investment,
        goal.delivered,
        isPerThousand(sub.pricingModel)
      ),
    })
  );
  // En el panel fijo también cuánto se lleva del objetivo.
  const goalItems =
    progress !== null
      ? [
          {
            key: "goalReached",
            label: t("clientView.stats.goalReached"),
            value: formatPercent(progress, language),
          },
        ]
      : [];
  const kpiLabel = t(`clientView.kpi.${sub.kpi}`);
  const siblings = campaign.subcampaigns;

  return (
    <ClientPage>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <ClientTrail
          items={[
            { label: t("clientView.home"), to: "/", returnTo: sub.id },
            {
              label: campaign.name,
              to: campaignPath(campaign.id),
              returnTo: sub.id,
            },
            { label: sub.name },
          ]}
        />
        <ClientHeader title={sub.name}>
          <PlatformTag platform={sub.platform} />
          <StatusPill status={sub.status} />
          <span>
            {formatDate(sub.startDate, language)} –{" "}
            {formatDate(sub.endDate, language)}
          </span>
          <span>{t("clientView.goalIs", { kpi: kpiLabel })}</span>
        </ClientHeader>
      </Box>

      <DateRangeBar
        value={range}
        onChange={setRange}
        bounds={bounds}
        yesterday={asOf}
      />

      {goal.planned && <GoalPanel goal={goal} kpiLabel={kpiLabel} />}

      <StickyStatsViewClient
        groups={[goalItems, performance, money.budget, money.costs]}
      >
        <StatPanels
          results={performance}
          investment={totals.investment}
          budget={totals.budget}
          currency={campaign.currency}
          costs={money.costs}
        />
      </StickyStatsViewClient>

      <MetricChartsViewClient
        days={days}
        kpi={sub.kpi}
        currency={campaign.currency}
        from={dateFrom}
        to={dateTo}
      />

      {detailLoading ? (
        <DetailSectionsLoading />
      ) : detail ? (
        <DetailSectionsViewClient
          detail={detail}
          currency={campaign.currency}
          from={dateFrom}
          to={dateTo}
          lifetime={range.preset === "lifetime"}
        />
      ) : null}

      {siblings.length > 1 && (
        <ClientCard title={t("clientView.subcampaign.siblings")}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
            {siblings.map((s) => {
              const current = s.id === sub.id;
              return (
                <Chip
                  key={s.id}
                  label={<span {...{ [TITLE_SOURCE_ATTR]: "" }}>{s.name}</span>}
                  component={RouterLink}
                  to={subcampaignPath(campaign.id, s.id)}
                  {...transitionLinkProps(
                    go,
                    subcampaignPath(campaign.id, s.id),
                    {
                      expand: true,
                    }
                  )}
                  clickable
                  variant={current ? "filled" : "outlined"}
                  color={current ? "primary" : "default"}
                  sx={{ maxWidth: 320, fontWeight: current ? 700 : 500 }}
                />
              );
            })}
          </Box>
        </ClientCard>
      )}
    </ClientPage>
  );
};

export default SubcampaignViewClient;
